import { Routes, CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserStateService, AppStateService, GlobalsService, PermissionsService } from './core/services';
import { PermissionModuleRef } from './core/models';
import { LocaleRedirectComponent } from './shared/components/locale-redirect/locale-redirect.component';

// Auth Guard — waits for session check to complete before evaluating
const authGuard: CanActivateFn = () => {
  const userState = inject(UserStateService);

  // If session is already checked, return immediately
  if (userState.sessionReady()) {
    return userState.isLoggedIn();
  }

  // Wait for checkSession() to finish, then check isLoggedIn
  return toObservable(userState.sessionReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => userState.isLoggedIn())
  );
};

// Designer Guard — requires designer role (admin, support, or designer user type).
// The flag derives from the PARENT customer's typeId, so a sub-user session
// (the human is a team member, not the staff parent) is always rejected.
const designerGuard: CanActivateFn = () => {
  const globals = inject(GlobalsService);
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  if (globals.isDesigner && permissions.canAccount()) {
    return true;
  }

  // Redirect non-designers to the root
  return router.createUrlTree(['/']);
};

// Staff Guard — gates /support/* routes. Allows entry when the *effective*
// user is staff (ADMIN, SUPPORT, CUSTOMER_SUPPORT, or PRODUCTION). "Effective" means the
// parked admin's typeId during impersonation, falling back to the active
// user. This way, an admin who's currently impersonating a customer can
// still navigate into the support panel without exiting impersonation —
// the impersonation banner stays visible there, and any actions inside
// can decide on a per-feature basis whether they're safe to run with the
// active customer token.
//
// Waits for sessionReady (mirrors authGuard) so a deep-link refresh
// doesn't bounce while typeIds are still being resolved.
const staffGuard: CanActivateFn = () => {
  const userState = inject(UserStateService);
  const globals = inject(GlobalsService);
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  const evaluate = (): boolean | ReturnType<Router['createUrlTree']> => {
    // Sub-user sessions never count as staff — the typeId below is the
    // PARENT customer's, not the team member's.
    if (!permissions.canAccount()) {
      return router.createUrlTree(['/']);
    }
    const effectiveTypeId =
      userState.originalUser()?.typeId ?? userState.user()?.typeId;
    return globals.isStaffTypeId(effectiveTypeId)
      ? true
      : router.createUrlTree(['/']);
  };

  if (userState.sessionReady()) {
    return evaluate();
  }
  return toObservable(userState.sessionReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => evaluate())
  );
};

// Album Design Guard — gates /album-design on the user.hasAlbumDesign feature flag.
// Waits for the session to finish loading so a deep-link refresh doesn't bounce
// while the user is still being fetched (mirrors galleriesGuard).
const albumDesignGuard: CanActivateFn = () => {
  const userState = inject(UserStateService);
  const router = inject(Router);

  const isAllowed = (): boolean | ReturnType<Router['createUrlTree']> =>
    userState.user()?.hasAlbumDesign === true ? true : router.createUrlTree(['/']);

  if (userState.sessionReady()) {
    return isAllowed();
  }
  return toObservable(userState.sessionReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => isAllowed())
  );
};

// Galleries Guard — requires user.allowGalleries. Waits for the session to
// finish loading first so a deep-link refresh doesn't bounce while the user
// is still being fetched.
const galleriesGuard: CanActivateFn = () => {
  const userState = inject(UserStateService);
  const router = inject(Router);

  const isAllowed = (): boolean | ReturnType<Router['createUrlTree']> =>
    userState.user()?.allowGalleries === true ? true : router.createUrlTree(['/']);

  if (userState.sessionReady()) {
    return isAllowed();
  }
  return toObservable(userState.sessionReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => isAllowed())
  );
};

// Module Guard factory — gates a route on sub-user permission modules
// (customer-users guide §4). Customer sessions are unrestricted; a sub-user
// needs at least ONE of the listed module refs. Waits for sessionReady so a
// deep-link refresh doesn't bounce while /users/me is still hydrating.
// Blocked-route bounce target: /:locale/home. A bare '/' resolves through
// LocaleRedirect → '/:locale' → '' → 'login', which shows the LOGIN FORM to a
// still-logged-in user — home is the honest destination.
const homeBounce = (router: Router, appState: AppStateService) =>
  router.createUrlTree(['/', appState.getLocale(), 'home']);

const moduleGuard = (...refs: PermissionModuleRef[]): CanActivateFn => () => {
  const userState = inject(UserStateService);
  const permissions = inject(PermissionsService);
  const appState = inject(AppStateService);
  const router = inject(Router);

  const evaluate = (): boolean | ReturnType<Router['createUrlTree']> =>
    refs.some(ref => permissions.hasModule(ref)) ? true : homeBounce(router, appState);

  if (userState.sessionReady()) {
    return evaluate();
  }
  return toObservable(userState.sessionReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => evaluate())
  );
};

// Main-account Guard — surfaces sub-users must never reach (profile,
// team management, …). ACCOUNT is not grantable, so this is simply
// "not a sub-user session".
const mainAccountGuard: CanActivateFn = () => {
  const userState = inject(UserStateService);
  const permissions = inject(PermissionsService);
  const appState = inject(AppStateService);
  const router = inject(Router);

  const evaluate = (): boolean | ReturnType<Router['createUrlTree']> =>
    permissions.canAccount() ? true : homeBounce(router, appState);

  if (userState.sessionReady()) {
    return evaluate();
  }
  return toObservable(userState.sessionReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => evaluate())
  );
};

const localeGuard: CanActivateFn = (route, state) => {
  const appState = inject(AppStateService);
  const globals = inject(GlobalsService);
  const translate = inject(TranslateService);
  const router = inject(Router);
  const locale = route.paramMap.get('locale');
  const supportedLocales = globals.languages.map(language => language.locale);
  const fallbackLocale = appState.locale() || 'pt_PT';

  if (!locale || !supportedLocales.includes(locale)) {
    const targetUrl = state.url.startsWith('/') ? state.url : `/${state.url}`;
    return router.parseUrl(`/${fallbackLocale}${targetUrl}`);
  }

  if (appState.locale() !== locale) {
    translate.use(locale);
    appState.setLocale(locale);
  }

  return true;
};

export const routes: Routes = [
  {
    path: 'g/:token',
    loadComponent: () => import('./features/galleries/gallery-viewer/gallery-viewer-shell.component')
      .then(m => m.GalleryViewerShellComponent)
  },
  {
    path: 'p/:photographerToken',
    loadComponent: () => import('./features/galleries/photographer-portal/photographer-portal.component')
      .then(m => m.PhotographerPortalComponent)
  },
  {
    path: 'share/:accessToken',
    loadComponent: () => import('./features/album-guest/album-guest-login.component')
      .then(m => m.AlbumGuestLoginComponent)
  },
  // Viva Smart Checkout return redirects — these exact paths (no locale)
  // are registered on the Viva payment source per environment. The page
  // relays t/s to the opener (the order-details popup flow) and closes.
  {
    path: 'vivapayments/success',
    loadComponent: () => import('./features/payment-result/viva-return.component')
      .then(m => m.VivaReturnComponent),
    data: { vivaStatus: 'success' }
  },
  {
    path: 'vivapayments/failure',
    loadComponent: () => import('./features/payment-result/viva-return.component')
      .then(m => m.VivaReturnComponent),
    data: { vivaStatus: 'fail' }
  },
  {
    // Bare (locale-less) reset link. MUST precede ':locale' — that param
    // route matches ANY first segment, so without this a bare
    // /reset-password?token=… binds locale='reset-password' and the
    // ''→login redirect chain eats the token.
    path: 'reset-password',
    loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
  },
  {
    path: ':locale',
    canActivate: [localeGuard],
    children: [
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full'
      },
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        // Public — target of password-recovery AND sub-user invite emails
        // (?token=…). Registered backend-side as URL_RECOVER_PASSWORD_<BRAND>.
        path: 'reset-password',
        loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
      },
      {
        path: 'album/preview/:accessToken',
        loadComponent: () => import('./features/album-guest/album-guest-login.component').then(m => m.AlbumGuestLoginComponent)
      },
      {
        // Locale-prefixed alias for the root `/share/:accessToken` route. The
        // share-panel and any future link-builder produce locale-prefixed
        // URLs (`/pt_PT/share/<token>`), so the same component must resolve
        // here too — otherwise the locale wildcard further down redirects
        // the guest to `home`.
        path: 'share/:accessToken',
        loadComponent: () => import('./features/album-guest/album-guest-login.component').then(m => m.AlbumGuestLoginComponent)
      },
      {
        path: 'home',
        loadComponent: () => import('./features/home/home.component').then(m => m.HomeComponent),
        canActivate: [authGuard]
      },
      {
        path: 'products',
        loadChildren: () => import('./features/products/products.routes').then(m => m.PRODUCTS_ROUTES),
        canActivate: [authGuard, moduleGuard('PRODUCTS')]
      },
      {
        // Tag campaign page (catalogue shortcut): an INDEX of the tagged items
        // that sends each one into its own category page — never a configurator.
        path: 'tags/:tagId',
        loadComponent: () => import('./features/tags/tag-page.component').then(m => m.TagPageComponent),
        canActivate: [authGuard, moduleGuard('PRODUCTS')]
      },
      {
        // Hosts both order-history tabs (ORDERS) and drafts/favorites tabs
        // (PROJECTS) — reachable with either module; the tab list itself is
        // filtered per-module inside OrdersComponent.
        path: 'orders',
        loadComponent: () => import('./features/orders/orders.component').then(m => m.OrdersComponent),
        canActivate: [authGuard, moduleGuard('ORDERS', 'PROJECTS')]
      },
      {
        // Order DETAIL deep link. CART is included because the post-checkout
        // confirmation (cart → /orders/:orderId?payInit…) must render for a
        // CART-without-ORDERS role, or the payment instructions are lost.
        // PROJECTS deliberately excluded — an order id is not a drafts surface.
        path: 'orders/:orderId',
        loadComponent: () => import('./features/orders/orders.component').then(m => m.OrdersComponent),
        canActivate: [authGuard, moduleGuard('ORDERS', 'CART')]
      },
      {
        path: 'cart',
        loadComponent: () => import('./features/cart/cart.component').then(m => m.CartComponent),
        canActivate: [authGuard, moduleGuard('CART')]
      },
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent),
        canActivate: [authGuard, mainAccountGuard]
      },
      {
        // Standalone invoices page — the profile Invoices section is
        // main-account-only, so INVOICES-granted sub-users reach it here.
        path: 'invoices',
        loadComponent: () => import('./features/profile/sections/profile-invoices/invoices-page.component')
          .then(m => m.InvoicesPageComponent),
        canActivate: [authGuard, moduleGuard('INVOICES')]
      },
      {
        path: 'projects',
        loadComponent: () => import('./features/projects/projects.component').then(m => m.ProjectsComponent),
        canActivate: [authGuard, moduleGuard('PROJECTS')]
      },
      {
        path: 'album-design',
        loadComponent: () => import('./features/album-design/album-design.component').then(m => m.AlbumDesignComponent),
        canActivate: [authGuard, albumDesignGuard, moduleGuard('ALBUM_DESIGN')]
      },
      {
        // Serves standard-draft paginations (PROJECTS), album-design
        // paginations (ALBUM_DESIGN), AND the products wizard's open-editor
        // step (PRODUCTS — a PRODUCTS+CART role designs the album project it
        // is ordering) — any of the three opens it.
        path: 'editor',
        loadComponent: () => import('./features/editor/editor.component').then(m => m.EditorComponent),
        canActivate: [authGuard, moduleGuard('PROJECTS', 'ALBUM_DESIGN', 'PRODUCTS')]
      },
      {
        path: 'uploads',
        loadComponent: () => import('./features/uploads/uploads.component').then(m => m.UploadsComponent),
        canActivate: [authGuard, moduleGuard('PROJECTS', 'ALBUM_DESIGN')]
      },
      {
        path: 'designer',
        loadComponent: () => import('./features/designer/designer.component').then(m => m.DesignerComponent),
        canActivate: [authGuard, designerGuard]
      },
      {
        path: 'support',
        loadComponent: () => import('./features/support/support-panel/support-panel.component')
          .then(m => m.SupportPanelComponent),
        canActivate: [authGuard, staffGuard]
      },
      {
        path: 'support/bug-reports',
        loadComponent: () => import('./features/support/bug-reports-list/bug-reports-list.component')
          .then(m => m.BugReportsListComponent),
        canActivate: [authGuard, staffGuard],
        data: { audience: 'admin' }
      },
      {
        path: 'support/bug-reports/:id',
        loadComponent: () => import('./features/support/bug-report-detail/bug-report-detail.component')
          .then(m => m.BugReportDetailComponent),
        canActivate: [authGuard, staffGuard],
        data: { audience: 'admin' }
      },
      // Customer area: the user's OWN bug reports + the support thread. Same
      // components as the admin screens, switched by `data.audience`; the
      // backend scopes /api/bug-reports to `customer_id = principal`, so no
      // module gate is needed (sub-users see the account's reports).
      {
        path: 'bug-reports',
        loadComponent: () => import('./features/support/bug-reports-list/bug-reports-list.component')
          .then(m => m.BugReportsListComponent),
        canActivate: [authGuard],
        data: { audience: 'customer' }
      },
      {
        path: 'bug-reports/:id',
        loadComponent: () => import('./features/support/bug-report-detail/bug-report-detail.component')
          .then(m => m.BugReportDetailComponent),
        canActivate: [authGuard],
        data: { audience: 'customer' }
      },
      {
        path: 'galleries',
        loadComponent: () => import('./features/galleries/galleries.component').then(m => m.GalleriesComponent),
        canActivate: [authGuard, galleriesGuard, moduleGuard('GALLERIES')]
      },
      {
        path: 'galleries/:galleryId/settings',
        loadChildren: () => import('./features/galleries/gallery-settings/gallery-settings.routes')
          .then(m => m.GALLERY_SETTINGS_ROUTES),
        canActivate: [authGuard, galleriesGuard, moduleGuard('GALLERIES')]
      },
      {
        path: 'galleries/:galleryId',
        loadComponent: () => import('./features/galleries/gallery-editor/gallery-editor.component').then(m => m.GalleryEditorComponent),
        canActivate: [authGuard, galleriesGuard, moduleGuard('GALLERIES')]
      },
      {
        path: 'payment-result',
        loadComponent: () => import('./features/payment-result/payment-result.component')
          .then(m => m.PaymentResultComponent),
        canActivate: [authGuard, moduleGuard('CART')]
      },
      {
        path: '**',
        redirectTo: 'home'
      }
    ]
  },
  {
    path: '**',
    loadComponent: () => LocaleRedirectComponent
  }
];
